import { getHeaders } from './authentication'
// GLOBALS
// Initial State
let initialState = {
  error: null,
  fetching: false,
  branches: [],
  teamMembers: [],
  branchLink: null,
  medpointIntegration: null,
}
// Action constants
export const resetBranchError = () => {
  return { type: 'RESET_ERROR' }
}

export const resetGenerateLink = () => {
  return { type: 'RESET_GENERATE_LINK' }
}

// Create Error
const SET_ERROR = 'SET_ERROR'

const ajaxActionFactory = (verb, url, method, data) => {
  return ({
    types: [verb, `${verb}_SUCCESS`, `${verb}_FAILURE`],
    payload: {
      request: {
        url, method, headers: getHeaders(), data
      }
    }
  })
}

//Get Geo Location
const GET_GEO_LOCATION = 'GET_GEO_LOCATION'
const GET_GEO_LOCATION_SUCCESS = 'GET_GEO_LOCATION_SUCCESS'
const GET_GEO_LOCATION_FAILURE = 'GET_GEO_LOCATION_FAILURE'

// Action creators
export const search = (searchTerm) => ajaxActionFactory(`SEARCH`, `/chains/search?searchTerm=${searchTerm}`, 'GET')
export const createNewBranch = (data) => ajaxActionFactory(`CREATE_BRANCH`, `/pharmacies/v2`, 'POST', data)
export const addBranchToOrg = (data) => ajaxActionFactory(`ADD_BRANCH_TO_ORG`, `/pharmacies/v2`, 'PUT', data)

export const getGeoLoc = (address) => {
  const { postcode, addressLine1, city, county, country } = address || {};
  const params = new URLSearchParams();
  if (postcode) params.set('postcode', postcode);
  if (addressLine1) params.set('addressLine1', addressLine1);
  if (city) params.set('city', city);
  if (county) params.set('county', county);
  if (country) params.set('country', country);

  return ({
    types: [GET_GEO_LOCATION, GET_GEO_LOCATION_SUCCESS, GET_GEO_LOCATION_FAILURE],
    payload: {
      request: {
        method: 'GET',
        headers: getHeaders(),
        url: `location/geolocation-by-postcode?${params.toString()}`,
      }
    }
  })
}

export const setError = (msg) => ({ type: SET_ERROR, payload: msg })

export const resolveBranchCoordinates = (branchDetails) => {
  return async (dispatch, getState) => {
    const address = branchDetails?.address;
    if (!address?.postcode && !address?.city) return null;

    const currentPostcode = getState().branches.selectedBranch?.postcode;
    const hasExistingCoords = branchDetails.coord_lat != null && branchDetails.coord_long != null;
    const postcodeChanged = !!(address.postcode && currentPostcode && address.postcode !== currentPostcode);

    if (hasExistingCoords && !postcodeChanged) return null;

    const result = await dispatch(getGeoLoc(address));
    const location = result.payload?.data?.location;
    const tier = result.payload?.data?.tier;

    if (!location?.latitude || !location.longitude) {
      return 'Could not resolve pharmacy coordinates from address — the server will retry on save.';
    }

    branchDetails.coord_lat = location.latitude;
    branchDetails.coord_long = location.longitude;

    if (tier && tier !== 'postcode') {
      return 'Pharmacy coordinates resolved approximately — please verify the address is correct.';
    }
    return null;
  }
}

export const prepareBranch = (branchDetails, branchID) => {
  return async (dispatch) => {
    const warning = await dispatch(resolveBranchCoordinates(branchDetails));
    if (warning) dispatch(setError(warning));

    if (branchID) return dispatch(updateBranch(branchID, { ...branchDetails }));
    return dispatch(addBranchToOrg({ ...branchDetails }));
  }
}

export const getBranch = (ID) => ajaxActionFactory(`GET_BRANCH`, `/pharmacies/V2/${ID}?includeCity=true`, 'GET')
export const generateLink = (pharmacyId) => ajaxActionFactory(
  `GENERATE_LINK`,
  `/invitations/pharmacies/invite-link?pharmacy_id=${pharmacyId}`,
  'GET'
);
export const getAllOrphanBranches = () => ajaxActionFactory(`GET_ALL_BRANCHES`, `/pharmacies?org=false`, 'GET')
export const getAllBranches = () => ajaxActionFactory(`GET_ALL_BRANCHES_WITHORG`, `/pharmacies?org=true`, 'GET')
export const getMedpointIntegration = (ID) => ajaxActionFactory('GET_MEDPOINT_INTEGRATION', `/integrations/medpoint/${ID}`, 'GET');
export const postMedpointIntegration = (data) => ajaxActionFactory('POST_MEDPOINT_INTEGRATION', '/integrations/medpoint', 'POST', data);
export const updateBranch = (ID, data) => ajaxActionFactory(`UPDATE_BRANCH`, `/pharmacies/V2/${ID}`, 'PUT', data)
export const removeTeamMember = (ID, teamMemberID) => ajaxActionFactory(`REMOVE_TEAM_MEMBER`, `/pharmacies/V2/${ID}?teamMember=${teamMemberID}`, 'DELETE')
export const addSurgeryPharmacyAssociation = (ID, data) => ajaxActionFactory(`ADD_SURGERY_PHARMACY_ASSOCIATION`, `/pharmacies/V2/${ID}/surgery`, 'PUT', data)
export const removeSurgeryPharmacyAssociation = (ID) => ajaxActionFactory(`REMOVE_SURGERY_PHARMACY_ASSOCIATION`, `/pharmacies/V2/${ID}/surgery`, 'DELETE')

function getActionError(action) {
  return action.error?.response?.data?.error?.text
  || action.error?.response?.data?.error
  || action.error?.response?.data?.message;
}

// Reducer
const branchesReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_ERROR:
      return { ...state, error: action.payload }
    case 'RESET_ERROR':
      return { ...state, error: null }
    case `CREATE_BRANCH`:
      return {
        ...state, fetching: true
      }
    case `CREATE_BRANCH_SUCCESS`:
      return {
        ...state, fetching: false
      }
    case `CREATE_BRANCH_FAILURE`:
      return {
        ...state, fetching: false
      }
    case `ADD_BRANCH_TO_ORG`:
      return {
        ...state,
        fetching: true
      }
    case `ADD_BRANCH_TO_ORG_SUCCESS`:
      return {
        ...state,
        fetching: false
      }
    case `ADD_BRANCH_TO_ORG_FAILURE`:
      return {
        ...state,
        fetching: false,
        error: getActionError(action)
      }
    case `GET_GEO_LOCATION`:
      return {
        ...state
      }
    case `GET_GEO_LOCATION_SUCCESS`:
      return {
        ...state,
      }
    case `GET_GEO_LOCATION_FAILURE`:
      return {
        ...state,
        error: getActionError(action)
      }
    case `GET_BRANCH`:
      return {
        ...state,
        showFilterIcon: false,
        fetching: true
      }
    case `GET_BRANCH_SUCCESS`:
      return {
        ...state,
        fetching: false,
        selectedBranch: action.payload.data.data
      }
    case `GET_BRANCH_FAILURE`:
      return {
        ...state,
        error: getActionError(action),
        fetching: false
      }
    case `GENERATE_LINK_SUCCESS`:
      return {
        ...state,
        branchLink: action.payload.data.data?.[0]?.invite_link
      }
    case `GENERATE_LINK_FAILURE`:
      return {
        ...state,
        error: getActionError(action),
      }
    case 'RESET_GENERATE_LINK':
      return {
        ...state,
        branchLink: null
      }
    case `GET_ALL_BRANCHES`:
      return {
        ...state, error: null
      }
    case `GET_ALL_BRANCHES_SUCCESS`:
      return {
        ...state,
        branches: action.payload.data.data
      }
    case `GET_ALL_BRANCHES_FAILURE`:
      return {
        ...state,
        branches: [],
        error: getActionError(action)
      }
    case `GET_ALL_BRANCHES_WITHORG`:
      return {
        ...state, error: null
      }
    case `GET_ALL_BRANCHES_WITHORG_SUCCESS`:
      return {
        ...state,
        branches: action.payload.data.data
      }
    case `GET_ALL_BRANCHES_WITHORG_FAILURE`:
      return {
        ...state,
        branches: [],
        error: getActionError(action)
      }
    case `GET_MEDPOINT_INTEGRATION`:
      return {
        ...state, error: null
      }
    case `GET_MEDPOINT_INTEGRATION_SUCCESS`:
      return {
        ...state,
        medpointIntegration: action.payload.data?.data?.length && action.payload.data.data[0]
      }
    case `GET_MEDPOINT_INTEGRATION_FAILURE`:
      return {
        ...state,
        medpointIntegration: [],
        error: getActionError(action)
      }
    case 'POST_MEDPOINT_INTEGRATION':
      return {
        ...state, error: null
      }
    case 'POST_MEDPOINT_INTEGRATION_SUCCESS':
      return {
        ...state,
        error: 'Integration Created Successfully'
      }
    case 'POST_MEDPOINT_INTEGRATION_FAILURE':
      return {
        ...state,
        medpointIntegration: [],
        error: getActionError(action)
      }
    case `UPDATE_BRANCH_SUCCESS`:
    return {
        ...state,
        selectedBranch: action.payload.data.pharmacy,
        error: "Updated Successfully"
      }
      case `UPDATE_BRANCH_FAILURE`:
      return {
        ...state,
        error: 'Problem Updating: ' + getActionError(action)
      }
    case `REMOVE_TEAM_MEMBER`:
      return {
        ...state
      }
    case `REMOVE_TEAM_MEMBER_SUCCESS`:
      return {
        ...state, error: 'Removed Successfully'
      }
    case `REMOVE_TEAM_MEMBER_FAILURE`:
      return {
        ...state,
        error: getActionError(action)
      }
    case `ADD_SURGERY_PHARMACY_ASSOCIATION`:
      return {
        ...state
      }
      case `ADD_SURGERY_PHARMACY_ASSOCIATION_SUCCESS`:
        return {
          ...state
        }
        case `ADD_SURGERY_PHARMACY_ASSOCIATION_FAILURE`:
      return {
        ...state,
        error: getActionError(action)
      }
    case `REMOVE_SURGERY_PHARMACY_ASSOCIATION`:
      return {
        ...state
      }
    case `REMOVE_SURGERY_PHARMACY_ASSOCIATION_SUCCESS`:
      return {
        ...state
      }
    case `REMOVE_SURGERY_PHARMACY_ASSOCIATION_FAILURE`:
      return {
        ...state,
        error: getActionError(action)
      }
    default:
      return state
  }
}

export default branchesReducer;
